import {memoize} from 'lodash';
import chunk from 'lodash/chunk';
import {decodeBlurHash} from 'fast-blurhash';
import tinycolor, {type ColorFormats} from 'tinycolor2';
import quantize from 'src/utils/quantize';

const getBackgroundColor = (colorHsl: ColorFormats.HSLA): number[] => {
  const color = tinycolor(colorHsl);

  const luminanceDeficit = 0.88 - colorHsl.l;
  const saturationExcess = colorHsl.s - 0.4;

  let newColor = color;

  if (luminanceDeficit > 0) {
    newColor = newColor.lighten(luminanceDeficit * 100);
  }

  if (saturationExcess > 0) {
    newColor = newColor.desaturate(saturationExcess * 100);
  }

  const newColorRgb = newColor.toRgb();
  return [newColorRgb.r, newColorRgb.g, newColorRgb.b];
};

const getDarkBackgroundColor = (colorHsl: ColorFormats.HSLA): number[] => {
  const color = tinycolor(colorHsl);

  const luminanceExcess = colorHsl.l - 0.15;

  const newColor = luminanceExcess > 0 ? color.darken(luminanceExcess * 100) : color;

  const newColorRgb = newColor.toRgb();

  return [newColorRgb.r, newColorRgb.g, newColorRgb.b];
};

const getTextColor = (colorHsl: ColorFormats.HSLA): number[] => {
  const color = tinycolor(colorHsl);

  const luminanceExcess = colorHsl.l - 0.2;

  const newColor = luminanceExcess > 0 ? color.darken(luminanceExcess * 100) : color;
  const newColorRgb = newColor.toRgb();

  return [newColorRgb.r, newColorRgb.g, newColorRgb.b];
};

const getDarkTextColor = (colorHsl: ColorFormats.HSLA): number[] => {
  const color = tinycolor({h: colorHsl.h, s: colorHsl.s + 0.5, l: colorHsl.l});

  const luminanceDeficit = 0.9 - colorHsl.l;

  const newColor = luminanceDeficit > 0 ? color.lighten(luminanceDeficit * 100) : color;

  const newColorRgb = newColor.toRgb();

  return [newColorRgb.r, newColorRgb.g, newColorRgb.b];
};

const getMostVibrantColor = (colors: [red: number, green: number, blue: number][]): number[] => {
  const contrasts = colors.map((color) => {
    const totalValue = color[0] + color[1] + color[2];
    const averageValue = totalValue / 3;

    const contrast = color.map((value) => Math.abs(value - averageValue)).reduce((acc, value) => acc + value, 0);

    return contrast;
  });

  const maxContrast = Math.max(...contrasts);
  const indexOfMostVibrantColor = contrasts.indexOf(maxContrast);

  return colors[indexOfMostVibrantColor];
};

type QuizColors = {
  bgColor: string;
  darkBgColor: string;
  textColor: string;
  darkTextColor: string;
  shadow: string;
  smallShadow: string;
};

const getCssVariables = ({
  bgColor,
  darkBgColor,
  textColor,
  darkTextColor,
  shadow,
  smallShadow
}: QuizColors): Record<string, string> => {
  return {
    '--quiz-bg-color': bgColor,
    '--dark-quiz-bg-color': darkBgColor,

    '--quiz-text-color': textColor,
    '--dark-quiz-text-color': darkTextColor,

    '--quiz-shadow': shadow,
    '--small-quiz-shadow': smallShadow
  };
};

type QuizColorsWithVariables = QuizColors & {
  cssVariables: Record<string, string>;
};

const getQuizColorsFromBlur = memoize((blurHash: string | undefined): QuizColorsWithVariables => {
  if (!blurHash) {
    const defaultColors = {
      bgColor: 'rgb(245, 245, 245)',
      darkBgColor: 'rgb(33, 28, 35)',
      textColor: 'rgb(37, 37, 37)',
      darkTextColor: 'rgb(255, 255, 255)',
      shadow: `
      1.6px 4.5px 3.6px rgba(33, 28, 35, 0.035),
      4.4px 12.5px 10px rgba(33, 28, 35, 0.05),
      10.6px 30.1px 24.1px rgba(33, 28, 35, 0.065),
      35px 100px 80px rgba(33, 28, 35, 0.1)
    `,
      smallShadow: `
      1.6px 4.5px 3.6px rgba(33, 28, 35, 0.035),
      4.4px 12.5px 10px rgba(33, 28, 35, 0.05),
      10.6px 30.1px 24.1px rgba(33, 28, 35, 0.065)
    `
    };

    return {
      cssVariables: getCssVariables(defaultColors),
      ...defaultColors
    };
  }

  const pixels = decodeBlurHash(blurHash, 12, 12);
  const colors = chunk(pixels, 4);

  const colorMap = quantize(colors, 3);
  const colorPalette = colorMap.palette();

  const mostVibrantColor = getMostVibrantColor(colorPalette);

  const color = tinycolor({r: mostVibrantColor[0], g: mostVibrantColor[1], b: mostVibrantColor[2]});
  const colorHsl = color.toHsl();

  const backgroundColor = getBackgroundColor(colorHsl);
  const darkBackgroundColor = getDarkBackgroundColor(colorHsl);

  const textColor = getTextColor(colorHsl);
  const darkTextColor = getDarkTextColor(colorHsl);

  const bgColorString = backgroundColor.join(',');
  const darkBgColorString = darkBackgroundColor.join(',');

  const quizColors = {
    bgColor: `rgb(${bgColorString})`,
    darkBgColor: `rgb(${darkBgColorString})`,
    textColor: `rgb(${textColor.join(',')})`,
    darkTextColor: `rgb(${darkTextColor.join(',')})`,

    shadow: `
      1.6px 4.5px 3.6px rgba(${darkBgColorString}, 0.035),
      4.4px 12.5px 10px rgba(${darkBgColorString}, 0.05),
      10.6px 30.1px 24.1px rgba(${darkBgColorString}, 0.065),
      35px 100px 80px rgba(${darkBgColorString}, 0.1)
    `,

    smallShadow: `
      1.6px 4.5px 3.6px rgba(${darkBgColorString}, 0.035),
      4.4px 12.5px 10px rgba(${darkBgColorString}, 0.05),
      10.6px 30.1px 24.1px rgba(${darkBgColorString}, 0.065)
    `
  };

  return {
    cssVariables: getCssVariables(quizColors),
    ...quizColors
  };
});

export default getQuizColorsFromBlur;
